import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

/**
 * Draw-on-screen signature pad. Returns a base64 PNG data URL via onChange.
 * Reusable across Delivery POD, Inward Log, Outward Log, and Drop workflow.
 */
export default function SignaturePad({ value = "", onChange, height = 140, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  // Setup canvas + high-DPI
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr; c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    // Restore existing value if provided
    if (value && !empty) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
    setDrawing(true); setEmpty(false);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    if (onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    if (onChange) onChange("");
  };

  return (
    <div className="border border-border rounded-md bg-white overflow-hidden" data-testid={testid}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height, touchAction: "none", display: "block", cursor: "crosshair" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-border bg-muted/30">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {empty ? "Sign above with mouse or finger" : "Signed ✓"}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={clear} data-testid={`${testid}-clear`} className="rounded-md h-6 text-[10px]">
          <Eraser size={10} className="mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}
